/**
 * Open Graph payload + DOM tags for product detail pages (share previews).
 */

import { getPrimaryProductImageUrl } from './productImages';

export const OG_IMAGE_WIDTH = '1200';
export const OG_IMAGE_HEIGHT = '630';

function upsertMetaProperty(property, content) {
  if (typeof document === 'undefined' || content == null || content === '') return;
  let el = document.querySelector(`meta[property="${property}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('property', property);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertMetaName(name, content) {
  if (typeof document === 'undefined' || content == null || content === '') return;
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertCanonicalLink(href) {
  if (typeof document === 'undefined' || !href) return;
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

/** Absolute URL; upgrade http → https for secure previews. */
export function toAbsoluteHttpsUrl(url) {
  if (url == null || typeof url === 'undefined') return '';
  const s = String(url).trim();
  if (!s) return '';
  if (/^https:\/\//i.test(s)) return s;
  if (/^http:\/\//i.test(s)) return s.replace(/^http:\/\//i, 'https://');
  if (s.startsWith('//')) return `https:${s}`;
  if (typeof window !== 'undefined' && window.location?.origin) {
    const origin = window.location.origin.replace(/^http:\/\//i, 'https://');
    return origin + (s.startsWith('/') ? s : `/${s}`);
  }
  return s;
}

export function buildProductOpenGraphPayload(product, options = {}) {
  if (!product || typeof product !== 'object') return null;

  const title = (product.name && String(product.name).trim()) || 'Product';
  const priceNum = product.price != null ? Number(product.price) : null;
  const priceLabel =
    priceNum != null && !Number.isNaN(priceNum)
      ? `Rs.${priceNum.toLocaleString()}`
      : '';
  const description =
    (options.description && String(options.description).trim()) ||
    (priceLabel ? `${title} — ${priceLabel}` : title);

  const imageRaw = getPrimaryProductImageUrl(product);
  const image = imageRaw ? toAbsoluteHttpsUrl(imageRaw) : '';

  const pageUrl =
    (options.pageUrl && String(options.pageUrl).trim()) ||
    (typeof window !== 'undefined' ? window.location.href : '');

  const canonicalUrl =
    (options.canonicalUrl && String(options.canonicalUrl).trim()) || pageUrl;

  const siteName =
    (options.siteName && String(options.siteName).trim()) || 'Super Wear';

  return {
    title,
    description,
    image,
    secureImage: image,
    url: pageUrl,
    canonicalUrl,
    siteName,
  };
}

/** Apply product OG / Twitter / description tags to document.head. */
export function applyProductOpenGraphTags(payload) {
  if (!payload || typeof document === 'undefined') return;

  upsertMetaProperty('og:type', 'product');
  upsertMetaProperty('og:title', payload.title);
  upsertMetaProperty('og:description', payload.description);
  upsertMetaProperty('og:site_name', payload.siteName);
  if (payload.url) upsertMetaProperty('og:url', payload.url);

  if (payload.image) {
    upsertMetaProperty('og:image', payload.image);
    upsertMetaProperty('og:image:secure_url', payload.secureImage || payload.image);
    upsertMetaProperty('og:image:width', OG_IMAGE_WIDTH);
    upsertMetaProperty('og:image:height', OG_IMAGE_HEIGHT);
    upsertMetaName('twitter:image', payload.image);
  }

  upsertMetaName('twitter:card', 'summary_large_image');
  upsertMetaName('twitter:title', payload.title);
  upsertMetaName('twitter:description', payload.description);
  upsertMetaName('description', payload.description);

  if (payload.canonicalUrl) upsertCanonicalLink(payload.canonicalUrl);

  document.title = `${payload.title} | ${payload.siteName}`;
}
